import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { getExamSessions } from "../../../services/ExamSessionService";
import { getMyStatistics } from "../../../services/StatisticsService";
import { getLocale } from "../../../utils/i18nLabels";
import { useYearFilters, YearFilters } from "../Statistics/YearFilters";
import { StatCard, Spinner, Panel, Empty } from "./DashboardParts";

// Tableau de bord de l'étudiant : uniquement SES examens et SES résultats
// publiés (le serveur ne renvoie jamais ceux des autres étudiants).
const StudentDashboard = () => {
  const { t } = useTranslation();
  const userName = useSelector((state) => state.auth.auth?.user?.email) || "";
  const filters = useYearFilters();

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    if (!filters.ready) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setLoadError("");
      try {
        const [statsRes, ongoingRes, scheduledRes] = await Promise.all([
          getMyStatistics(filters.paramsWithYear),
          getExamSessions({ page: 1, limit: 5, status: "ongoing" }),
          getExamSessions({ page: 1, limit: 5, status: "scheduled" }),
        ]);
        if (cancelled) return;
        setStats(statsRes.data?.data || null);
        setSessions([...(ongoingRes.data?.data || []), ...(scheduledRes.data?.data || [])].slice(0, 5));
      } catch (err) {
        if (!cancelled) setLoadError(err?.response?.data?.message || t("pages.dashboard.examsLoadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.ready, filters.key]);

  if (!filters.ready || (loading && !stats)) return <Spinner label={t("common.crud.loading")} />;

  const r = stats?.results;
  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="bg-welcome-banner mb-3">
          <div className="card-body">
            <h4 className="mb-0 text-white">{t("pages.dashboard.welcome", { name: userName })}</h4>
          </div>
        </div>

        <div className="card mb-3">
          <div className="card-body py-3">
            <YearFilters filters={filters} small show={["year", "semester", "subject", "exam", "period"]} />
          </div>
        </div>

        {loadError && <div className="alert alert-warning">{loadError}</div>}

        <div className="row">
          <StatCard title={t("pages.filters.averageOutOf20")} value={r?.averageOutOf20 != null ? `${r.averageOutOf20} / 20` : null} icon="ti ti-target-arrow" color="primary" />
          <StatCard title={t("pages.filters.bestScore")} value={r?.bestOutOf20 != null ? `${r.bestOutOf20} / 20` : null} icon="ti ti-trophy" color="warning" />
          <StatCard title={t("pages.teacherDashboard.passed")} value={r?.passed} icon="ti ti-circle-check" color="success"
            subtitle={t("pages.statistics.kpiResultsSuffix", { count: r?.total ?? 0 })} />
          <StatCard title={t("pages.teacherDashboard.failed")} value={r?.failed} icon="ti ti-circle-x" color="danger" />
        </div>

        {stats?.yearDecision && (
          <div className="alert alert-info">
            {t("pages.filters.yearDecision")} : <strong>{t(`pages.statistics.decision_${stats.yearDecision.decision}`)}</strong>
            {stats.yearDecision.decision === "admitted_with_credits" && ` (${stats.yearDecision.creditsToRetake})`}
          </div>
        )}

        <div className="row">
          <Panel title={t("common.ui.examensDisponibles")} action={<Link to="/my-exams" className="btn btn-sm btn-primary">{t("pages.dashboard.viewMyExams")}</Link>}>
            {sessions.length ? (
              <div className="list-group list-group-flush">
                {sessions.map((s) => (
                  <div key={s.id} className="list-group-item d-flex justify-content-between align-items-center">
                    <div>
                      <p className="mb-0 fw-medium">{t("pages.dashboard.session")} {new Date(s.start_at).toLocaleString(getLocale())}</p>
                      <small className="text-muted">{t("pages.dashboard.jusqu")} {new Date(s.end_at).toLocaleString(getLocale())}</small>
                    </div>
                    <span className={`badge badge-soft-${s.status === "ongoing" ? "primary" : "info"}`}>
                      {t(`common.examSessionStatus.${s.status}`)}
                    </span>
                  </div>
                ))}
              </div>
            ) : <Empty text={t("pages.dashboard.aucunExamenDisponiblePourMoment")} />}
          </Panel>

          <Panel title={t("pages.filters.myAverageBySubject")}>
            <div className="table-responsive">
              <table className="table mb-0">
                <thead className="thead-light">
                  <tr><th>{t("common.fields.subject")}</th><th>{t("pages.filters.averageOutOf20")}</th></tr>
                </thead>
                <tbody>
                  {stats?.bySubject?.length ? stats.bySubject.map((row) => (
                    <tr key={row.subjectId}>
                      <td className="fw-medium">{row.subjectName}</td>
                      <td>{row.averageOutOf20 != null ? `${row.averageOutOf20} / 20` : "-"}</td>
                    </tr>
                  )) : <tr><td colSpan="2" className="text-center text-muted py-3">{t("pages.teacherDashboard.noResults")}</td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>

        <div className="row">
          <Panel col="col-12" title={t("pages.teacherDashboard.latestResults")} action={<Link to="/results" className="btn btn-sm btn-outline-primary">{t("pages.dashboard.viewAll")}</Link>}>
            <div className="table-responsive">
              <table className="table mb-0">
                <thead className="thead-light">
                  <tr>
                    <th>{t("common.ui.examen")}</th>
                    <th>{t("common.fields.subject")}</th>
                    <th>{t("common.ui.score")}</th>
                    <th>{t("common.crud.statusColumn")}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats?.recent?.length ? stats.recent.map((x) => (
                    <tr key={x.id}>
                      <td className="fw-medium">{x.examTitle}</td>
                      <td>{x.subjectName || "-"}</td>
                      <td>{x.scoreOutOf20 != null ? `${Number(x.scoreOutOf20).toFixed(2)} / 20` : `${x.scorePercentage}%`}</td>
                      <td><span className={`badge badge-soft-${x.passed ? "success" : "danger"}`}>{x.passed ? t("pages.teacherDashboard.passed") : t("pages.teacherDashboard.failed")}</span></td>
                    </tr>
                  )) : <tr><td colSpan="4" className="text-center text-muted py-3">{t("pages.teacherDashboard.noResults")}</td></tr>}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
